import { Button } from "@/components/ui/button";

import {
    ArForm,
    ArFormField,
    ArTextAreaFormField,
} from "@/v2/components/ui/form";

import { CreateArProfileSchema } from "@/v2/schema/ar-profile";

import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import LoadingAnimation from "../../ui/LoadingAnimation";
import FindArProfile from "@/v2/api/ar-profile/Find";
import CreateArProfile from "@/v2/api/ar-profile/Create";
import UpdateArProfile from "@/v2/api/ar-profile/Update";
import { useNavigate } from "react-router-dom";

export default function ArProfileForm({ id }: { id?: string }) {
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const query = useQuery({
        queryKey: ["ar-profile"],
        queryFn: () => FindArProfile(),
        enabled: !!id,
    });

    const form = useForm<CreateArProfileSchema>({
        resolver: zodResolver(CreateArProfileSchema),
        defaultValues: {
            company_code: "",
            company_name: "",
            address: "",
            city: "",
            state: "",
            area: "",
            country: "",
            pincode: "",
            building: "",
            phone_number: "",
            email: "",
            tax_reg_no: "",
            website: "",
            iban: "",
            account_number: "",
            bank_name: "",
            bank_address: "",
            representative_person: {
                full_name: "",
                email: "",
                phone_number: "",
            },
        },
    });

    const { reset } = form;

    useEffect(() => {
        if (query.data) {
            reset(query.data);
        }
    }, [query.data, reset]);

    const CreateMutation = useMutation({
        mutationFn: (data: CreateArProfileSchema) => CreateArProfile(data),
    });

    const EditMutation = useMutation({
        mutationFn: (data: CreateArProfileSchema) =>
            UpdateArProfile({ id: id!, data }),
    });

    const handleFormSubmit = (data: CreateArProfileSchema) => {
        const mutation = id ? EditMutation : CreateMutation;
        const action = id ? "updated" : "created";

        mutation.mutate(data, {
            onError: () => {
                toast.error("Something went wrong", {
                    description: `Failed while ${action} the AR Profile.`,
                });
            },
            onSuccess: () => {
                toast.success(`AR Profile ${action} successfully`);
                queryClient.invalidateQueries({ queryKey: ["ar-profile"] });
                navigate(`/account/company-details`);
            },
        });
    };

    if (id && query.isLoading) return <LoadingAnimation />;

    if (id && query.isError) {
        return (
            <div className="flex items-center justify-center p-8">
                <div className="text-center">
                    <p className="mb-4 text-red-600">Failed to load profile</p>
                </div>
            </div>
        );
    }

    return (
        <ArForm
            form={form}
            formSchema={CreateArProfileSchema}
            onSubmit={handleFormSubmit}
        >
            <div className="space-y-4">
                {/* Company Info */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <ArFormField<CreateArProfileSchema>
                        label="Company Code"
                        name="company_code"
                        placeholder="Enter company code"
                    />
                    <ArFormField<CreateArProfileSchema>
                        label="Company Name"
                        name="company_name"
                        placeholder="Enter company name"
                    />
                </div>

                <ArTextAreaFormField<CreateArProfileSchema>
                    label="Address"
                    name="address"
                    rows={3}
                />

                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <ArFormField<CreateArProfileSchema>
                        label="City"
                        name="city"
                    />
                    <ArFormField<CreateArProfileSchema>
                        label="State"
                        name="state"
                    />
                    <ArFormField<CreateArProfileSchema>
                        label="Area"
                        name="area"
                    />
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <ArFormField<CreateArProfileSchema>
                        label="Country"
                        name="country"
                    />
                    <ArFormField<CreateArProfileSchema>
                        label="Pincode"
                        name="pincode"
                    />
                    <ArFormField<CreateArProfileSchema>
                        label="Building"
                        name="building"
                    />
                </div>

                {/* Contact */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <ArFormField<CreateArProfileSchema>
                        label="Phone Number"
                        name="phone_number"
                    />
                    <ArFormField<CreateArProfileSchema>
                        label="Email"
                        name="email"
                    />
                </div>

                <ArFormField<CreateArProfileSchema>
                    label="Tax Registration Number"
                    name="tax_reg_no"
                />

                <ArFormField<CreateArProfileSchema>
                    label="Website"
                    name="website"
                />

                {/* Banking */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <ArFormField<CreateArProfileSchema>
                        label="IBAN"
                        name="iban"
                    />
                    <ArFormField<CreateArProfileSchema>
                        label="Account Number"
                        name="account_number"
                    />
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <ArFormField<CreateArProfileSchema>
                        label="Bank Name"
                        name="bank_name"
                    />
                    <ArFormField<CreateArProfileSchema>
                        label="Bank Address"
                        name="bank_address"
                    />
                </div>

                {/* Representative */}
                <h3 className="pt-4 font-semibold">Representative</h3>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <ArFormField<CreateArProfileSchema>
                        label="Name"
                        name="representative_person.full_name"
                    />
                    <ArFormField<CreateArProfileSchema>
                        label="Email"
                        name="representative_person.email"
                    />
                    <ArFormField<CreateArProfileSchema>
                        label="Phone"
                        name="representative_person.phone_number"
                    />
                </div>

                <div className="flex justify-end gap-2 border-t pt-4">
                    <Button
                        type="submit"
                        className="min-w-[160px]"
                        disabled={
                            CreateMutation.isPending || EditMutation.isPending
                        }
                    >
                        {CreateMutation.isPending || EditMutation.isPending
                            ? "Saving..."
                            : id
                              ? "Update Profile"
                              : "Create Profile"}
                    </Button>
                </div>
            </div>
        </ArForm>
    );
}
